/**
 * Card Navigation Context Utility
 * Stores card list context in sessionStorage to enable left/right navigation in CardDetail
 */

const STORAGE_KEY = 'cardNavigationContext';

/**
 * Set the card navigation context
 * @param {Object} context - The navigation context
 * @param {string} context.type - Type of context: 'collection', 'home-post', 'wishlist', 'binder', 'search'
 * @param {string[]} context.cardIds - Array of card IDs in order
 * @param {number} context.currentIndex - Current card index in the array
 * @param {string} [context.sourceId] - Optional source identifier (post ID, binder ID, etc.)
 * @param {string} [context.sourceLabel] - Optional label for back navigation
 */
export const setCardNavigationContext = (context) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...context,
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.error('Failed to set card navigation context:', e);
  }
};

/**
 * Get the current card navigation context
 * @returns {Object|null} The navigation context or null if not set/expired
 */
export const getCardNavigationContext = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const context = JSON.parse(stored);

    // Expire context after 30 minutes of inactivity
    const thirtyMinutes = 30 * 60 * 1000;
    if (Date.now() - context.timestamp > thirtyMinutes) {
      clearCardNavigationContext();
      return null;
    }

    return context;
  } catch (e) {
    console.error('Failed to get card navigation context:', e);
    return null;
  }
};

/**
 * Clear the card navigation context
 */
export const clearCardNavigationContext = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear card navigation context:', e);
  }
};

/**
 * Update the current index in the context and increment navigation count
 * @param {number} newIndex - The new current index
 */
export const updateContextIndex = (newIndex) => {
  const context = getCardNavigationContext();
  if (context) {
    setCardNavigationContext({
      ...context,
      currentIndex: newIndex,
      navCount: (context.navCount || 0) + 1,
    });
  }
};

/**
 * Get the number of navigations within the current context
 * @returns {number} Number of prev/next navigations done
 */
export const getNavCount = () => {
  const context = getCardNavigationContext();
  return context?.navCount || 0;
};

/**
 * Get navigation info for the current card
 * @param {string} cardId - The current card ID to check against context
 * @returns {Object} Navigation info: { hasPrev, hasNext, prevId, nextId, currentIndex, total, contextType }
 */
export const getNavigationInfo = (cardId) => {
  const context = getCardNavigationContext();

  if (!context || !context.cardIds || context.cardIds.length === 0) {
    return {
      hasPrev: false,
      hasNext: false,
      prevId: null,
      nextId: null,
      currentIndex: -1,
      total: 0,
      contextType: null,
    };
  }

  // Find current card in the list
  let currentIndex = context.cardIds.indexOf(cardId);

  // If not found, use the stored index
  if (currentIndex === -1) {
    currentIndex = context.currentIndex || 0;
  }

  const total = context.cardIds.length;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < total - 1;

  return {
    hasPrev,
    hasNext,
    prevId: hasPrev ? context.cardIds[currentIndex - 1] : null,
    nextId: hasNext ? context.cardIds[currentIndex + 1] : null,
    currentIndex,
    total,
    contextType: context.type,
    sourceLabel: context.sourceLabel,
  };
};

/**
 * Helper to set context and navigate
 * @param {Function} navigate - React Router navigate function
 * @param {Object} context - The context object
 * @param {string} cardId - The card ID to navigate to
 * @param {string} [suffix] - Optional URL suffix (e.g., '?view=zoom')
 */
export const navigateWithContext = (navigate, context, cardId, suffix = '') => {
  setCardNavigationContext(context);
  navigate(`/card/${cardId}${suffix}`);
};
